<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { UiDialog, UiIcon, UiText } from '@shopexperts/shared';

const props = defineProps<{
  expertName: string;
  initialFile?: File | null;
  loading?: boolean;
  errorMessage?: string | null;
}>();

const emit = defineEmits<{
  next: [file: File];
  close: [];
}>();

const open = defineModel<boolean>({ default: false });

const MAX_BYTES = 10 * 1024 * 1024;

const REQUIRED_HEADERS = [
  'source_platform', 'source_review_url', 'source_review_id', 'review_date',
  'reviewer_email', 'reviewer_first_name', 'reviewer_last_name', 'reviewer_display_name',
  'store_name', 'store_url', 'overall_rating', 'quality', 'communication', 'timelines',
  'value_for_money', 'review_text', 'project_name', 'project_value_range_label', 'is_anonymous',
];

const file = ref<File | null>(props.initialFile ?? null);
const dragActive = ref(false);
const localError = ref<string | null>(null);
const showHeaders = ref(false);
const inputEl = ref<HTMLInputElement | null>(null);

watch(() => props.initialFile, (next) => {
  if (next instanceof File) file.value = next;
});

watch(open, (isOpen) => {
  if (!isOpen) {
    localError.value = null;
    dragActive.value = false;
  }
});

const fileSize = computed(() => {
  if (!file.value) return '';
  const bytes = file.value.size;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
});

const displayError = computed(() => localError.value || props.errorMessage || null);

const canProceed = computed(() => !!file.value && !props.loading);

function pickFile() {
  inputEl.value?.click();
}

function validateFile(candidate: File): string | null {
  const looksCsv = candidate.type === 'text/csv'
    || candidate.type === 'application/vnd.ms-excel'
    || candidate.name.toLowerCase().endsWith('.csv');
  if (!looksCsv) return 'Please select a .csv file.';
  if (candidate.size === 0) return 'The selected file is empty.';
  if (candidate.size > MAX_BYTES) return 'File exceeds the 10 MB limit.';
  return null;
}

function setFile(candidate: File | null | undefined) {
  if (!candidate) return;
  const err = validateFile(candidate);
  if (err) {
    localError.value = err;
    return;
  }
  localError.value = null;
  file.value = candidate;
}

function onInputChange(e: Event) {
  const target = e.target as HTMLInputElement;
  setFile(target.files?.[0]);
  target.value = '';
}

function onDrop(e: DragEvent) {
  e.preventDefault();
  dragActive.value = false;
  setFile(e.dataTransfer?.files?.[0]);
}

function onDragOver(e: DragEvent) {
  e.preventDefault();
  dragActive.value = true;
}

function onDragLeave() {
  dragActive.value = false;
}

function clearFile(e: Event) {
  e.stopPropagation();
  file.value = null;
  localError.value = null;
}

function handleNext() {
  if (!file.value) return;
  emit('next', file.value);
}

function handleClose() {
  open.value = false;
  emit('close');
}
</script>

<template>
  <UiDialog
    v-model="open"
    :title="`Import Reviews — ${expertName}`"
    text="Upload a CSV to preview the rows before importing."
    size="md"
    @close="handleClose"
  >
    <div class="flex flex-col gap-5">
      <!-- Drop zone -->
      <button
        type="button"
        @click="pickFile"
        @drop="onDrop"
        @dragover="onDragOver"
        @dragleave="onDragLeave"
        class="group relative w-full rounded-2xl border-2 border-dashed transition-all duration-200 overflow-hidden cursor-pointer text-left"
        :class="[
          dragActive
            ? 'border-emerald-400 bg-emerald-50/40'
            : file
              ? 'border-emerald-200 bg-white hover:bg-emerald-50/30'
              : 'border-gray-200 bg-gray-50/60 hover:border-gray-300 hover:bg-gray-50',
        ]"
      >
        <input
          ref="inputEl"
          type="file"
          accept=".csv,text/csv,application/vnd.ms-excel"
          class="hidden"
          @change="onInputChange"
        />
        <div class="px-6 py-8 flex flex-col items-center gap-3 text-center">
          <div
            class="w-14 h-14 rounded-2xl flex items-center justify-center transition-all"
            :class="file ? 'bg-emerald-100 text-emerald-700' : 'bg-white border border-gray-200 text-gray-400 group-hover:text-gray-600'"
          >
            <UiIcon :name="file ? 'file-placeholder' : 'upload'" class="w-7 h-7" />
          </div>

          <template v-if="file">
            <UiText as="p" variant="h6" class="font-semibold text-gray-900 max-w-full truncate">
              {{ file.name }}
            </UiText>
            <div class="flex items-center gap-2 text-xs text-gray-500">
              <span class="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium tracking-wide uppercase text-[10px]">CSV</span>
              <span class="tabular-nums">{{ fileSize }}</span>
              <span class="text-gray-300">·</span>
              <button
                type="button"
                class="text-gray-500 hover:text-gray-800 transition-colors underline-offset-2 hover:underline"
                @click="clearFile"
              >
                Remove
              </button>
              <span class="text-gray-300">·</span>
              <span class="text-gray-500 group-hover:text-gray-800 transition-colors">Replace…</span>
            </div>
          </template>
          <template v-else>
            <UiText as="p" variant="h6" class="font-medium text-gray-700">
              Drop a CSV here, or click to browse
            </UiText>
            <UiText as="p" variant="caption" class="text-gray-400">
              Up to 10 MB · UTF-8 encoded
            </UiText>
          </template>
        </div>
      </button>

      <!-- Required columns disclosure -->
      <div class="rounded-xl border border-gray-100 bg-gray-50/60">
        <button
          type="button"
          class="w-full flex items-center justify-between px-4 py-3 text-left"
          @click="showHeaders = !showHeaders"
        >
          <span class="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
            <span class="w-1.5 h-1.5 rounded-full bg-gray-400" />
            Required CSV columns
          </span>
          <UiIcon
            name="chevron-down"
            class="w-4 h-4 text-gray-400 transition-transform"
            :class="{ 'rotate-180': showHeaders }"
          />
        </button>
        <div v-if="showHeaders" class="px-4 pb-4 -mt-1">
          <div class="flex flex-wrap gap-1.5">
            <code
              v-for="header in REQUIRED_HEADERS"
              :key="header"
              class="px-2 py-1 rounded bg-white border border-gray-200 text-[11px] font-mono text-gray-700 leading-none"
            >
              {{ header }}
            </code>
          </div>
        </div>
      </div>

      <!-- Error -->
      <div
        v-if="displayError"
        class="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 flex items-start gap-3"
      >
        <UiIcon name="close" class="w-4 h-4 text-rose-500 mt-0.5 shrink-0" />
        <div class="flex-1 min-w-0">
          <UiText as="p" variant="p-small" class="font-semibold text-rose-700">
            We couldn't parse this file
          </UiText>
          <UiText as="p" variant="p-small" class="text-rose-600 wrap-break-word">
            {{ displayError }}
          </UiText>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex flex-col gap-3">
        <button
          type="button"
          class="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-gray-900 text-white text-sm font-medium tracking-wide hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow"
          :disabled="!canProceed"
          @click="handleNext"
        >
          <span v-if="loading" class="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
          <span>{{ loading ? 'Parsing CSV…' : 'Next: Preview Rows' }}</span>
          <UiIcon v-if="!loading" name="arrow-right" class="w-4 h-4" />
        </button>
        <button
          type="button"
          class="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors"
          @click="handleClose"
        >
          Cancel
        </button>
      </div>
    </template>
  </UiDialog>
</template>
